/**
 * Widget bridge - DOM rendering helpers used by apps/widget.
 *
 * Kept separate so widget UI styles and templates can be reused by
 * tests, storybook, or future native SDKs without dragging Vite along.
 */

export * from './popup'

export interface MountOptions {
  appKey: string
  apiBaseUrl: string
  /** Optional click handler. Triggered in addition to the default
   *  console.info diagnostic. Pass this when you want the bubble to toggle
   *  a panel, etc. */
  onClick?: () => void
  /**
   * Launcher background. Falls back to the brand green when omitted. Only
   * `#RRGGBB` is accepted — the value lands in a stylesheet, and anything
   * containing `}` or `;` would let a tenant break out of the rule.
   */
  primary?: string
  /**
   * Image to show in the launcher instead of the built-in glyph. Falls back
   * to the SVG when omitted or blank.
   */
  avatarUrl?: string
}

/** Guard a theme colour before it is interpolated into the stylesheet. */
function safeHex(value: string | undefined, fallback: string): string {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback
}

/**
 * Guard a tenant-supplied media URL before it reaches an `src`. Only http(s)
 * and root-relative `/media/` paths pass; `javascript:`, `data:` and
 * protocol-relative `//host` are dropped so the browser never resolves them.
 */
function safeMediaUrl(value: string | undefined): string | null {
  if (!value) return null
  const v = value.trim()
  if (!v) return null
  if (v.startsWith('/media/')) return v
  if (/^https?:\/\//i.test(v)) return v
  return null
}

const BUBBLE_STYLE = `
:host { all: initial; /* How far the launcher is lifted off the bottom edge on
  narrow viewports. Pages that dock a fixed bar (mobile sticky CTA, cookie
  bar, floating contact form) would otherwise sit under the launcher. */
  --pc-lift: 0px; }
@media (max-width: 768px) { :host { --pc-lift: 64px; } }
.bubble {
  width: 56px; height: 56px; border-radius: 50%;
  background: var(--pc-bubble-bg, #1B5E3F); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: system-ui, sans-serif; font-size: 24px; font-weight: 700;
  cursor: pointer; box-shadow: 0 6px 16px rgba(0,0,0,0.25);
  user-select: none;
  border: 0; padding: 0;
  overflow: hidden;
  transform: translateY(calc(-1 * var(--pc-lift, 0px)));
}
.bubble:hover { filter: brightness(0.9); }
.bubble:focus-visible { outline: 3px solid #38bdf8; outline-offset: 2px; }
.bubble svg { width: 28px; height: 28px; display: block; }
.bubble img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

/**
 * Speech-bubble glyph for the launcher, built with createElementNS rather
 * than an innerHTML string so the icon can never carry markup from
 * anywhere near a tenant-supplied value.
 */
function buildChatIcon(): SVGElement {
  const NS = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(NS, 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('fill', 'none')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')

  const body = document.createElementNS(NS, 'path')
  body.setAttribute(
    'd',
    'M20 11.5a7.5 7.5 0 0 1-7.5 7.5H8.4L4 21.6V11.5A7.5 7.5 0 0 1 11.5 4h1A7.5 7.5 0 0 1 20 11.5Z',
  )
  body.setAttribute('stroke', 'currentColor')
  body.setAttribute('stroke-width', '2')
  body.setAttribute('stroke-linecap', 'round')
  body.setAttribute('stroke-linejoin', 'round')

  for (const cx of [8.75, 12, 15.25]) {
    const dot = document.createElementNS(NS, 'circle')
    dot.setAttribute('cx', String(cx))
    dot.setAttribute('cy', '11.5')
    dot.setAttribute('r', '1.15')
    dot.setAttribute('fill', 'currentColor')
    svg.appendChild(dot)
  }
  svg.appendChild(body)
  return svg
}

export interface BubbleController {
  /** Remove the button and its stylesheet from the shadow root. */
  destroy(): void
}

export function mountBubble(shadow: ShadowRoot, opts: MountOptions): BubbleController {
  const primary = safeHex(opts.primary, '#1B5E3F')
  const style = document.createElement('style')
  style.textContent = BUBBLE_STYLE
  shadow.appendChild(style)

  const bubble = document.createElement('button')
  bubble.type = 'button'
  bubble.className = 'bubble'
  bubble.setAttribute('role', 'button')
  bubble.setAttribute('aria-label', 'Open ProChat')
  // Custom property, not an inline `background:` — the stylesheet rule wins
  // over an inline style in the cascade order we care about here, so the
  // hover rule keeps working.
  bubble.style.setProperty('--pc-bubble-bg', primary)
  const avatar = safeMediaUrl(opts.avatarUrl)
  if (avatar) {
    const img = document.createElement('img')
    img.src = avatar
    img.alt = ''
    img.setAttribute('aria-hidden', 'true')
    // A broken image URL must not leave an empty circle: fall back to the
    // glyph rather than a blank button.
    img.addEventListener('error', () => {
      img.remove()
      if (!bubble.querySelector('svg')) bubble.appendChild(buildChatIcon())
    })
    bubble.appendChild(img)
  } else {
    bubble.appendChild(buildChatIcon())
  }
  if (opts.onClick) {
    bubble.addEventListener('click', () => opts.onClick!())
  } else {
    bubble.addEventListener('click', () => {
      // eslint-disable-next-line no-console
      console.info('[ProChat] open chat (Phase 1)', opts)
    })
  }
  shadow.appendChild(bubble)

  return {
    destroy() {
      bubble.remove()
      style.remove()
    },
  }
}